import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Calendar, Clock, Plus, Trash2, Save, Loader2, Users, ChevronLeft, ChevronRight,
  User, Flame
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DashboardLayout } from '../components/DashboardLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
const HEATMAP_HOURS = Array.from({ length: 15 }, (_, i) => i + 6); // 6am-8pm

export default function AvailabilityCalendar() {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);
  const [allAvailability, setAllAvailability] = useState([]);
  const [heatmapData, setHeatmapData] = useState(null);
  const [weekStart, setWeekStart] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCg, setSelectedCg] = useState(null);
  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);
  const isCaregiver = user?.role === 'caregiver';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [availRes, heatRes] = await Promise.all([
          axios.get(`${API_URL}/api/availability`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/staff-availability-heatmap`, { withCredentials: true }),
        ]);
        setAllAvailability(availRes.data);
        setHeatmapData(heatRes.data);
        setWeekStart(heatRes.data.week_start);
      } else {
        const { data } = await axios.get(`${API_URL}/api/availability`, { withCredentials: true });
        setSlots(data.slots || []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const navigateWeek = async (dir) => {
    const ws = new Date(weekStart);
    ws.setDate(ws.getDate() + dir * 7);
    const newStart = ws.toISOString().split('T')[0];
    setWeekStart(newStart);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/staff-availability-heatmap?week_start=${newStart}`, { withCredentials: true });
      setHeatmapData(data);
    } catch (err) { console.error(err); }
  };

  const goThisWeek = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/staff-availability-heatmap`, { withCredentials: true });
      setHeatmapData(data);
      setWeekStart(data.week_start);
    } catch (err) { console.error(err); }
  };

  const addSlot = () => setSlots(p => [...p, { day: 'monday', start: '09:00', end: '17:00' }]);
  const removeSlot = (i) => setSlots(p => p.filter((_, idx) => idx !== i));
  const updateSlot = (i, f, v) => setSlots(p => p.map((s, idx) => idx === i ? { ...s, [f]: v } : s));

  const saveAvailability = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/availability`, { slots }, { withCredentials: true });
      toast.success('Availability saved!');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const getCellColor = (avail, booked) => {
    if (avail === 0 && booked === 0) return { bg: 'bg-muted/30', text: 'text-muted-foreground/40' };
    if (avail === 0 && booked > 0) return { bg: 'bg-red-500/20', text: 'text-red-400' };
    if (booked === 0) return { bg: 'bg-[#A8E6CF]/20', text: 'text-[#A8E6CF]' };
    const ratio = booked / avail;
    if (ratio >= 1) return { bg: 'bg-red-500/25', text: 'text-red-400' };
    if (ratio >= 0.7) return { bg: 'bg-amber-500/20', text: 'text-amber-400' };
    if (ratio >= 0.4) return { bg: 'bg-[#00A6A0]/15', text: 'text-[#00A6A0]' };
    return { bg: 'bg-[#A8E6CF]/20', text: 'text-[#A8E6CF]' };
  };

  if (loading) return <DashboardLayout><div className="p-6 flex justify-center"><div className="spinner w-8 h-8" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="availability-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              {isAdmin ? 'Staff Availability' : 'My Availability'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {isAdmin ? `${heatmapData?.total_caregivers || 0} caregiver(s) with availability set` : 'Set your weekly available hours'}
            </p>
          </div>
          {isCaregiver && (
            <Button className="btn-primary gap-2" onClick={saveAvailability} disabled={saving} data-testid="save-availability-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </Button>
          )}
        </div>

        {/* ======= ADMIN HEATMAP VIEW ======= */}
        {isAdmin && heatmapData && (
          <>
            {/* Week Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={() => navigateWeek(-1)} data-testid="heatmap-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" className="border-border text-foreground text-xs h-8" onClick={goThisWeek} data-testid="heatmap-today">
                This Week
              </Button>
              <Button variant="outline" size="icon" className="h-8 w-8 border-border" onClick={() => navigateWeek(1)} data-testid="heatmap-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="text-sm text-foreground font-medium ml-2" data-testid="heatmap-range">
                {heatmapData.week_start} — {heatmapData.week_end}
              </span>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-[#A8E6CF]/20" /><span>Mostly Free</span></div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-[#00A6A0]/15" /><span>Some Booked</span></div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-amber-500/20" /><span>Filling Up</span></div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-red-500/25" /><span>Fully Booked</span></div>
              <div className="flex items-center gap-1.5"><div className="w-4 h-3 rounded-sm bg-muted/30" /><span>No Availability</span></div>
            </div>

            {/* Heatmap Grid */}
            <Card className="glass-card border-border overflow-hidden">
              <div className="overflow-x-auto">
                <div style={{ minWidth: '700px' }}>
                  {/* Header row */}
                  <div className="grid gap-0" style={{ gridTemplateColumns: '72px repeat(15, 1fr)' }}>
                    <div className="p-2 bg-muted/50 border-b border-r border-border" />
                    {HEATMAP_HOURS.map(h => (
                      <div key={h} className="p-1.5 bg-muted/50 border-b border-border text-center">
                        <span className="text-[10px] text-muted-foreground font-medium">{h.toString().padStart(2, '0')}</span>
                      </div>
                    ))}
                  </div>

                  {/* Day rows */}
                  {DAYS.map((day, di) => (
                    <div key={day} className="grid gap-0" style={{ gridTemplateColumns: '72px repeat(15, 1fr)' }}>
                      <div className="p-2 border-b border-r border-border bg-muted/30 flex items-center">
                        <span className="text-xs text-foreground font-medium">{DAY_SHORT[di]}</span>
                      </div>
                      {HEATMAP_HOURS.map(h => {
                        const hStr = `${h.toString().padStart(2, '0')}:00`;
                        const cell = heatmapData.heatmap?.[day]?.[hStr] || { available: 0, booked: 0, free: 0 };
                        const colors = getCellColor(cell.available, cell.booked);
                        return (
                          <div
                            key={h}
                            className={`border-b border-border p-1 ${colors.bg} transition-colors hover:ring-1 hover:ring-white/10 cursor-default group relative`}
                            data-testid={`heatmap-${day}-${hStr}`}
                            title={`${DAY_SHORT[di]} ${hStr} | Available: ${cell.available} | Booked: ${cell.booked} | Free: ${cell.free}`}
                          >
                            <div className="flex flex-col items-center justify-center h-8">
                              {cell.available > 0 || cell.booked > 0 ? (
                                <>
                                  <span className={`text-[10px] font-bold ${colors.text}`}>{cell.free}</span>
                                  <span className="text-[8px] text-muted-foreground">{cell.booked}/{cell.available}</span>
                                </>
                              ) : (
                                <span className="text-[9px] text-muted-foreground/30">—</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-4 py-2 border-t border-border bg-muted/30 text-[10px] text-muted-foreground">
                Numbers show: <strong>free</strong> caregivers (booked/available). Hover for details.
              </div>
            </Card>

            {/* Per-Caregiver Detail */}
            <Card className="glass-card border-border">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="text-sm font-heading text-foreground flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#00A6A0]" />
                  Individual Schedules ({heatmapData.caregivers?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3">
                {!heatmapData.caregivers?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No caregivers have set their availability yet.</p>
                ) : (
                  <div className="space-y-2">
                    {heatmapData.caregivers.map(cg => (
                      <details key={cg.user_id} className="group" data-testid={`cg-schedule-${cg.user_id}`}>
                        <summary className="flex items-center justify-between p-3 rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#FF6F61]/10 flex items-center justify-center">
                              <User className="w-4 h-4 text-[#FF6F61]" />
                            </div>
                            <div>
                              <p className="text-sm text-foreground font-medium">{cg.user_name}</p>
                              <p className="text-[10px] text-muted-foreground">
                                {Object.keys(cg.days).length} day(s) configured
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="px-3 pb-3 pl-14">
                          <div className="grid grid-cols-7 gap-1.5">
                            {DAYS.map((day, di) => {
                              const dayData = cg.days[day] || { available: [], booked: [] };
                              const hasData = dayData.available.length > 0 || dayData.booked.length > 0;
                              return (
                                <div key={day} className="text-center">
                                  <p className="text-[10px] text-muted-foreground mb-1">{DAY_SHORT[di]}</p>
                                  {!hasData ? (
                                    <div className="h-10 rounded bg-muted/30 flex items-center justify-center">
                                      <span className="text-[9px] text-muted-foreground/40">Off</span>
                                    </div>
                                  ) : (
                                    <div className="space-y-0.5">
                                      {dayData.available.map((s, i) => (
                                        <div key={`a-${i}`} className="px-1 py-0.5 rounded bg-[#A8E6CF]/10 border border-[#A8E6CF]/20">
                                          <p className="text-[9px] text-[#A8E6CF]">{s.start}-{s.end}</p>
                                        </div>
                                      ))}
                                      {dayData.booked.map((s, i) => (
                                        <div key={`b-${i}`} className="px-1 py-0.5 rounded bg-amber-500/10 border border-amber-500/20">
                                          <p className="text-[9px] text-amber-400">{s.start}-{s.end}</p>
                                          <p className="text-[8px] text-muted-foreground truncate">{s.client_name}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* ======= CAREGIVER EDIT VIEW ======= */}
        {isCaregiver && (
          <>
            <div className="space-y-3">
              {slots.map((slot, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border" data-testid={`slot-${i}`}>
                  <Select value={slot.day} onValueChange={(v) => updateSlot(i, 'day', v)}>
                    <SelectTrigger className="bg-muted border-border text-foreground w-36" data-testid={`slot-day-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map(d => (
                        <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={slot.start} onValueChange={(v) => updateSlot(i, 'start', v)}>
                    <SelectTrigger className="bg-muted border-border text-foreground w-24" data-testid={`slot-start-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <span className="text-muted-foreground text-sm">to</span>

                  <Select value={slot.end} onValueChange={(v) => updateSlot(i, 'end', v)}>
                    <SelectTrigger className="bg-muted border-border text-foreground w-24" data-testid={`slot-end-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <Button variant="ghost" size="icon" onClick={() => removeSlot(i)} className="text-red-400 hover:text-red-300" data-testid={`remove-slot-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              <Button variant="outline" onClick={addSlot} className="border-border text-foreground hover:bg-muted gap-2" data-testid="add-slot-btn">
                <Plus className="w-4 h-4" /> Add Time Slot
              </Button>
            </div>

            {/* Weekly summary */}
            {slots.length > 0 && (
              <Card className="glass-card border-border">
                <CardHeader className="pb-2 border-b border-border">
                  <CardTitle className="text-sm font-heading text-foreground">Your Weekly Schedule</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-7 gap-2">
                    {DAYS.map((day, di) => {
                      const daySlots = slots.filter(s => s.day === day);
                      return (
                        <div key={day} className="text-center">
                          <p className="text-xs text-muted-foreground mb-2">{DAY_SHORT[di]}</p>
                          {daySlots.length === 0 ? (
                            <div className="h-12 rounded bg-muted/30 border border-border flex items-center justify-center">
                              <span className="text-[10px] text-muted-foreground">Off</span>
                            </div>
                          ) : (
                            daySlots.map((s, i) => (
                              <div key={i} className="mb-1 p-1.5 rounded bg-[#A8E6CF]/10 border border-[#A8E6CF]/30">
                                <p className="text-[10px] text-[#A8E6CF] font-medium">{s.start}-{s.end}</p>
                              </div>
                            ))
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
